import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Bot, Upload, ScanLine, CreditCard, Landmark, BarChart3,
  ArrowLeft, Building2, Users, FileText, X, ChevronRight, Check
} from 'lucide-react';

type Path = 'paths' | 'migration' | 'new_society' | 'exploring';

const FEATURES = [
  { icon: Bot,       label: 'PD Orbit',         color: 'bg-blue-50 text-blue-600' },
  { icon: Upload,    label: 'Smart Migration',   color: 'bg-indigo-50 text-indigo-600' },
  { icon: ScanLine,  label: 'OCR Scanner',       color: 'bg-violet-50 text-violet-600' },
  { icon: CreditCard,label: 'NoCash Payments',   color: 'bg-emerald-50 text-emerald-600' },
  { icon: Landmark,  label: 'Treasury',          color: 'bg-blue-50 text-blue-700' },
  { icon: BarChart3, label: 'Reports',           color: 'bg-slate-100 text-slate-600' },
];

const NEW_SOCIETY_STEPS = [
  { icon: Building2, label: 'Create blocks',    path: '/dashboard/blocks',   hint: 'North Block, South Block, Tower A…' },
  { icon: FileText,  label: 'Add flat numbers', path: '/dashboard/flats',    hint: 'N-101, N-102, S-201…' },
  { icon: Users,     label: 'Add residents',    path: '/dashboard/residents', hint: 'Name, phone, flat assignment' },
  { icon: CreditCard,label: 'Generate bills',   path: '/dashboard/billing-summary', hint: 'Monthly maintenance demands' },
];

interface Props {
  onClose: () => void;
}

export default function OnboardingModal({ onClose }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Path>('paths');
  const orbitShortcut = useMemo(() => (navigator.platform.toUpperCase().includes('MAC') ? '⌘K' : 'Ctrl + K'), []);

  const finish = (path?: string) => {
    onClose();
    if (path) navigate(path);
  };

  const openOrbit = () => {
    onClose();
    setTimeout(() => window.dispatchEvent(new CustomEvent('pd-orbit:open')), 250);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* ── Welcome / path selection ───────────────────────────────────── */}
        {step === 'paths' && (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 px-6 pt-8 pb-6 text-white relative">
              <button
                onClick={() => finish()}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
                title="Skip"
              >
                <X size={16} />
              </button>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Welcome to PropertyDirect</h2>
                  <p className="text-[11px] text-blue-200 font-medium mt-0.5">Everything you need to manage your society.</p>
                </div>
              </div>
              {/* Feature pills */}
              <div className="flex flex-wrap gap-1.5 mt-4">
                {FEATURES.map(f => (
                  <div key={f.label} className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2.5 py-1">
                    <f.icon size={11} className="text-white/70" />
                    <span className="text-[10px] font-semibold text-white/90">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Path selection */}
            <div className="px-6 py-5">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">How are you getting started?</p>
              <div className="space-y-2">
                <PathCard
                  icon={Upload}
                  iconClass="bg-indigo-50 text-indigo-600"
                  title="Moving from another system"
                  subtitle="Import your existing residents, bills, and ledger data"
                  onClick={() => setStep('migration')}
                />
                <PathCard
                  icon={Building2}
                  iconClass="bg-blue-50 text-blue-600"
                  title="Creating a new society"
                  subtitle="Set up blocks, flats, residents, and start billing"
                  onClick={() => setStep('new_society')}
                />
                <PathCard
                  icon={Sparkles}
                  iconClass="bg-violet-50 text-violet-600"
                  title="Just exploring"
                  subtitle="Take a guided tour with PD Orbit, your AI copilot"
                  onClick={() => setStep('exploring')}
                />
              </div>
              <button
                onClick={() => finish()}
                className="w-full text-center text-[11px] text-slate-400 hover:text-slate-600 mt-4 py-1.5 transition-colors"
              >
                Skip for now
              </button>
              <div className="flex items-center justify-center gap-1.5 mt-1 pb-1">
                <kbd className="px-1.5 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-400 rounded border border-slate-200">{orbitShortcut}</kbd>
                <span className="text-[9px] text-slate-400 ml-1">opens PD Orbit anytime</span>
              </div>
            </div>
          </>
        )}

        {/* ── Migration path ─────────────────────────────────────────────── */}
        {step === 'migration' && (
          <>
            <PathHeader title="Import your existing data" onBack={() => setStep('paths')} onClose={() => finish()} />
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Smart Migration supports Excel and CSV files. AI automatically maps your columns and flags anything that needs review — nothing is imported until you approve it.
              </p>
              <div className="space-y-2">
                {['Residents & flat assignments', 'Maintenance bills & ledger', 'Water & electricity bills', 'Opening balances & dues'].map(item => (
                  <div key={item} className="flex items-center gap-2.5">
                    <Check size={14} className="text-emerald-500 shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="pt-1">
                <div className="flex gap-2.5 text-[10px] text-slate-500 mb-4">
                  {['Excel (.xlsx, .xls)', 'CSV', 'AI column mapping'].map(f => (
                    <span key={f} className="bg-slate-100 rounded-lg px-2.5 py-1 font-semibold">{f}</span>
                  ))}
                </div>
                <button
                  onClick={() => finish('/dashboard/smart-import')}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white rounded-xl text-sm font-bold transition-colors shadow-sm shadow-indigo-500/20"
                >
                  Start Migration →
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── New society path ────────────────────────────────────────────── */}
        {step === 'new_society' && (
          <>
            <PathHeader title="Set up your society" onBack={() => setStep('paths')} onClose={() => finish()} />
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-slate-600">Follow these steps to get your society ready for billing:</p>
              <div className="space-y-1.5">
                {NEW_SOCIETY_STEPS.map((s, i) => (
                  <button
                    key={s.label}
                    onClick={() => finish(s.path)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-primary hover:bg-blue-50/50 text-left transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-primary/10 flex items-center justify-center shrink-0 transition-colors">
                      <s.icon size={14} className="text-slate-500 group-hover:text-primary transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}.</span>
                        <span className="text-sm font-semibold text-slate-800 group-hover:text-primary transition-colors">{s.label}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 pl-6 mt-0.5">{s.hint}</p>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 group-hover:text-primary transition-colors shrink-0" />
                  </button>
                ))}
              </div>
              <button
                onClick={() => finish('/dashboard')}
                className="w-full text-center text-[11px] text-slate-400 hover:text-slate-600 mt-2 py-1.5 transition-colors"
              >
                Start from overview
              </button>
            </div>
          </>
        )}

        {/* ── Exploring path ──────────────────────────────────────────────── */}
        {step === 'exploring' && (
          <>
            <PathHeader title="Meet PD Orbit" onBack={() => setStep('paths')} onClose={() => finish()} />
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                PD Orbit is your AI Operations Copilot — it can look up residents, check payment status, analyse revenue, and tell you exactly what to do today.
              </p>
              <div className="space-y-1.5">
                {[
                  'What should I do today?',
                  'Show pending dues this month',
                  'Check treasury balances',
                  'Who hasn\'t paid maintenance?',
                ].map(q => (
                  <div key={q} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                    <Bot size={12} className="text-blue-500 shrink-0" />
                    <span className="text-xs text-slate-600 italic">&ldquo;{q}&rdquo;</span>
                  </div>
                ))}
              </div>
              <div className="pt-1 space-y-2">
                <button
                  onClick={openOrbit}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-opacity shadow-sm shadow-blue-500/20"
                >
                  Ask PD Orbit
                </button>
                <button
                  onClick={() => finish('/dashboard')}
                  className="w-full py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-sm font-semibold transition-colors"
                >
                  Explore on my own
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function PathCard({ icon: Icon, iconClass, title, subtitle, onClick }: {
  icon: React.ElementType; iconClass: string; title: string; subtitle: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border border-slate-200 hover:border-primary hover:bg-blue-50/40 text-left transition-colors group"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconClass}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 group-hover:text-primary transition-colors">{title}</div>
        <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{subtitle}</div>
      </div>
      <ChevronRight size={15} className="text-slate-300 group-hover:text-primary transition-colors shrink-0" />
    </button>
  );
}

function PathHeader({ title, onBack, onClose }: { title: string; onBack: () => void; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium">
        <ArrowLeft size={15} />
        Back
      </button>
      <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100">
        <X size={15} />
      </button>
    </div>
  );
}
